/**
 * Modal dialog / bottom sheet: portal, scrim, focus trap, Escape to close and
 * focus restoration to whatever was focused before it opened (UX-SPEC §7).
 * Scale+fade in 200ms (a slide-up for sheets); reduced motion → plain fade.
 */
import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import clsx from 'clsx';
import { focusMain } from '../lib/focus';
import { IconClose } from './Icons';
import './Modal.css';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function focusableIn(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute('hidden') && el.getAttribute('aria-hidden') !== 'true' && el.tabIndex !== -1,
  );
}

/** Keep Tab / Shift+Tab inside `root`. */
export function trapTab(e: KeyboardEvent, root: HTMLElement | null): void {
  if (e.key !== 'Tab' || !root) return;
  const items = focusableIn(root);
  if (items.length === 0) {
    e.preventDefault();
    return;
  }
  const first = items[0]!;
  const last = items[items.length - 1]!;
  const active = document.activeElement;
  if (e.shiftKey && (active === first || !root.contains(active))) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && (active === last || !root.contains(active))) {
    e.preventDefault();
    first.focus();
  }
}

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** Visually hide the title (it still labels the dialog). */
  hideTitle?: boolean;
  description?: ReactNode;
  /** Id of an element inside `children` that describes the dialog (used when there is no `description`). */
  describedBy?: string;
  children: ReactNode;
  footer?: ReactNode;
  variant?: 'dialog' | 'sheet';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  /** Element to focus first (defaults to the first focusable control). */
  initialFocus?: RefObject<HTMLElement | null>;
  /** Scrim clicks close the dialog (default true). */
  dismissible?: boolean;
  role?: 'dialog' | 'alertdialog';
}

export function Modal({ open, ...rest }: ModalProps) {
  return createPortal(<AnimatePresence>{open && <ModalPanel {...rest} />}</AnimatePresence>, document.body);
}

function ModalPanel({
  onClose,
  title,
  hideTitle = false,
  description,
  describedBy,
  children,
  footer,
  variant = 'dialog',
  size = 'md',
  className,
  initialFocus,
  dismissible = true,
  role = 'dialog',
}: Omit<ModalProps, 'open'>) {
  const titleId = useId();
  const descId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  // Focus in on open; restore focus to the opener on close.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    // The first content control, not the header's Close button (its attribute
    // value is "", so test for presence, not truthiness). Close only when the
    // dialog has nothing else to focus; the panel itself as a last resort.
    const items = panel ? focusableIn(panel) : [];
    const target = initialFocus?.current ?? items.find((el) => !el.hasAttribute('data-modal-close')) ?? items[0] ?? panel;
    target?.focus({ preventScroll: true });
    return () => {
      // Only restore when focus is still ours (or lost to <body>): when another
      // dialog replaced this one (menu → resign), it owns focus now.
      const active = document.activeElement;
      const ours = !active || active === document.body || (panel?.contains(active) ?? false);
      if (!ours) return;
      // The opener may have unmounted meanwhile (the action bar a finished
      // game replaced), or been <body> itself: fall back to the main content.
      if (opener && opener !== document.body && opener.isConnected && typeof opener.focus === 'function') {
        opener.focus({ preventScroll: true });
      } else {
        focusMain();
      }
    };
    // Runs once per open: the panel mounts when opened and unmounts on close.
  }, []);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      e.preventDefault();
      closeRef.current();
      return;
    }
    trapTab(e, panelRef.current);
  };

  const sheet = variant === 'sheet';
  return (
    <div className={clsx('modal-root', sheet && 'modal-root--sheet')} onKeyDown={onKeyDown}>
      <motion.div
        className="modal-scrim"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={dismissible ? () => closeRef.current() : undefined}
        aria-hidden="true"
      />
      <motion.div
        ref={panelRef}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : describedBy}
        tabIndex={-1}
        className={clsx('modal', `modal--${size}`, sheet && 'modal--sheet', className)}
        initial={sheet ? { y: '100%' } : { opacity: 0, scale: 0.96, y: 8 }}
        animate={sheet ? { y: 0 } : { opacity: 1, scale: 1, y: 0 }}
        exit={sheet ? { y: '100%' } : { opacity: 0, scale: 0.97, y: 4 }}
        transition={sheet ? { type: 'spring', stiffness: 420, damping: 40 } : { duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
      >
        <header className={clsx('modal__head', hideTitle && 'modal__head--bare')}>
          <h2 id={titleId} className={clsx('modal__title', hideTitle && 'sr-only')}>
            {title}
          </h2>
          <button
            type="button"
            className="btn btn--ghost btn--icon btn--sm modal__close"
            onClick={() => closeRef.current()}
            aria-label="Close"
            data-modal-close=""
          >
            <IconClose />
          </button>
        </header>
        {description && (
          <p id={descId} className="modal__desc">
            {description}
          </p>
        )}
        <div className="modal__body">{children}</div>
        {footer && <footer className="modal__foot">{footer}</footer>}
      </motion.div>
    </div>
  );
}
